/**
 * voiceRenderingSlice.js
 *
 * Phase 4c Minimum — Item 6b. Read-only client state backing the
 * BookWorksheet "Dry Resolve" panel.
 *
 * Phase 4d Step 3 — Rendering Studio R1 (this file). Adds the per-character
 * "eye-test grid" surface: list characters, fetch their CharSpeechSample
 * grid (with last-rendered cell metadata), kick off a sequential TTS run,
 * poll job progress, and resign expired signed URLs without re-rendering.
 *
 * Consumes the endpoints exposed by voiceRenderingController:
 *
 *   POST /api/voice-rendering/dry-resolve                             (4c)
 *   GET  /api/voice-rendering/:bookId/snapshot                        (4c)
 *   GET  /api/voice-rendering/:bookId/characters                      (4d)
 *   GET  /api/voice-rendering/:bookId/grid/:characterUuid             (4d)
 *   POST /api/voice-rendering/:bookId/render-grid                     (4d)
 *   GET  /api/voice-rendering/grid-progress/:jobId                    (4d)
 *   POST /api/voice-rendering/cell/:cellId/resign                     (4d)
 *
 * Phase 4d R3a — selector-driven render-unit pipeline (this batch §6.4):
 *
 *   GET  /api/voice-rendering/:bookId/render-tree                     (4d-R3a)
 *   GET  /api/voice-rendering/:bookId/preflight?selector=…            (4d-R3a)
 *   POST /api/voice-rendering/:bookId/render-unit  body:{selector}    (4d-R3a)
 *   GET  /api/voice-rendering/unit-progress/:jobId                    (4d-R3a)
 *   GET  /api/voice-rendering/:bookId/rendered-lines?selector=…       (4d-R3a)
 *   GET  /api/voice-rendering/rendered-line/:lineUuid/url             (4d-R3a)
 *
 * Shape mirrors voiceStatsSlice.js so we get identical patterns (per-bookId
 * entries, 30s TTL, short-circuit thunk conditions, unwrap of the
 * `{ success, data }` wrapper). Deliberately separate from voiceStatsSlice
 * because this slice is action-heavy (lines array resolution) and the
 * cacheable unit is (bookId × scriptVersion × linesHash), not a single
 * per-book blob.
 *
 * Thunks (4c — dry resolve):
 *   - fetchBookRenderSnapshot({ bookId, scriptVersion?, fileName?, force? })
 *   - dryResolveLines({ bookId, lines, scriptVersion?, fileName? })
 *
 * Thunks (4d — Rendering Studio):
 *   - fetchRenderCharacters(bookId)
 *   - fetchCharacterGrid({ bookId, characterUuid })
 *   - startGridRender({ bookId, characterUuid })  → 202 + initial job snapshot
 *   - pollGridProgress(jobId)                     → idempotent progress poll
 *   - resignRenderedCell(cellId)                  → cheap 1h URL refresh
 *
 * Thunks (4d-R3a — selector-driven render units):
 *   - fetchRenderTree({ bookId, scriptVersion?, fileName?, force? })
 *   - fetchRenderPreflight({ bookId, selector, scriptVersion?, fileName? })
 *   - startUnitRender({ bookId, selector, force?, scriptVersion?, fileName? })
 *       → 202 with initial job snapshot; 409 with `{ preflight }` when RED.
 *   - pollUnitProgress(jobId)
 *   - fetchRenderedLines({ bookId, selector, scriptVersion?, fileName? })
 *   - resignRenderedLineUrl(lineUuid)
 *
 * Reducer actions:
 *   - clearDryResolve({ bookId }) — wipes `resolve` for a book; chunk that
 *     consumes this panel dispatches it on book change or tab close.
 *   - clearVoiceRenderingError({ bookId, scope })
 *   - clearRenderJob() — resets `renderJob.current` (use on page unmount or
 *     when the user picks a different character).
 *   - clearRenderTree({ bookId }) — wipes the cached selector-tree (book change).
 *   - clearPreflight({ selector? }) — single-selector or full reset.
 *   - clearUnitJob() — mirrors `clearRenderJob` for selector-driven jobs.
 *   - clearRenderedLines({ selector? }) — single-selector or full reset.
 */


import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

// ---------------------------------------------------------------------------
// Shared helpers (verbatim parity with voiceStatsSlice.js)
// ---------------------------------------------------------------------------
const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

/**
 * FLATTEN THE SERVER'S ERROR ENVELOPE, or its diagnosis never reaches the operator.
 *
 * voiceRenderingController.fail() sends `{ success:false, error:{ code, message } }`, and every
 * component reads `e.message` — which is one level up and therefore undefined. So a 409 saying
 *
 *     No stitched chapters for selector "book.full" — run the stitch first.
 *
 * arrived on screen as "Export failed", and the actual cause (the body was stitched as
 * `book.body`, the export asked for `book.full`) had to be found by reading the controller.
 *
 * Flattened here rather than at each call site: `handle` is the one gate every thunk passes
 * through, and a component that already reads `.message` keeps working unchanged. `code` is
 * carried up too, so a caller can branch on NO_CHAPTERS without matching on prose.
 */
const handle = async (response, rejectWithValue) => {
  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch (_) {
      body = null;
    }
    const inner = (body && body.error && typeof body.error === 'object') ? body.error : body;
    return rejectWithValue({
      ...(inner || {}),
      message: (inner && inner.message) || (body && body.message) || response.statusText || 'Request failed',
      code: (inner && inner.code) || (body && body.code) || null,
      status: response.status,
    });
  }
  return response.json();
};

const unwrap = (payload) => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data;
  }
  return payload;
};

const TTL_MS = 30 * 1000;
const isFresh = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && (Date.now() - t) < TTL_MS;
};

const ensureBookEntry = (state, bookId) => {
  if (!state.byBook[bookId]) {
    state.byBook[bookId] = {
      snapshot: null,
      scriptVersion: null,    // server-reported picked version for the snapshot
      scriptLoaded: false,
      scriptLoadError: null,
      resolve: {
        results: [],
        snapshot: null,
        scriptVersion: null,
        scriptLoaded: false,
        scriptLoadError: null,
        requestedLineCount: 0,
        receivedAt: null
      },
      loading: { snapshot: false, resolve: false },
      error: { snapshot: null, resolve: null },
      lastFetched: { snapshot: null }
    };
  }
  return state.byBook[bookId];
};

// Build a final URL from a routerConfig template + path params. Mirrors the
// ad-hoc `.replace(':bookId', bookId)` used elsewhere in this slice but
// generalizes to multiple params so the new Rendering Studio thunks can use
// templates with two slots (e.g. `:bookId/grid/:characterUuid`).
const buildUrl = (template, params = {}) => {
  let out = template;
  Object.entries(params).forEach(([k, v]) => {
    out = out.replace(`:${k}`, encodeURIComponent(String(v)));
  });
  return out;
};


// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * GET /api/voice-rendering/:bookId/snapshot?scriptVersion=|fileName=
 * Read-only view of what the resolver currently sees (character/variation
 * counts, neutral-voice presence per character). Backs the panel's
 * readiness header.
 */
export const fetchBookRenderSnapshot = createAsyncThunk(
  'voiceRendering/fetchBookRenderSnapshot',
  async ({ bookId, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const qs = new URLSearchParams();
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const url = config.GET_VOICE_RENDERING_SNAPSHOT.replace(':bookId', bookId) + suffix;
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchBookRenderSnapshot] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchBookRenderSnapshot] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  },
  {
    condition: ({ bookId, scriptVersion, fileName, force } = {}, { getState }) => {
      if (!bookId) return false;
      if (force) return true;
      const entry = getState().voiceRendering?.byBook?.[bookId];
      if (entry?.loading?.snapshot) return false;
      if (!isFresh(entry?.lastFetched?.snapshot)) return true;
      // Version pin changed — force refetch regardless of TTL.
      const cached = entry?.scriptVersion;
      if (scriptVersion &&
        cached?.uuid !== scriptVersion &&
        cached?.version !== String(scriptVersion)
      ) {
        return true;
      }
      if (fileName && cached?.fileName !== fileName) return true;
      return false;
    }
  }
);

/**
 * POST /api/voice-rendering/dry-resolve
 * Body: { bookId, lines, scriptVersion?, fileName? }
 *
 * `lines` normalization is tolerant: accepts an array of
 * `{ id?, speaker, emotion }` OR a single `{ speaker, emotion }` payload.
 * The server mirrors this shape.
 */
export const dryResolveLines = createAsyncThunk(
  'voiceRendering/dryResolveLines',
  async (
    { bookId, lines, speaker, emotion, scriptVersion, fileName } = {},
    { rejectWithValue }
  ) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });

    const body = { bookId };
    if (Array.isArray(lines) && lines.length > 0) {
      body.lines = lines;
    } else if (typeof speaker === 'string') {
      body.speaker = speaker;
      body.emotion = emotion || '';
    } else {
      return rejectWithValue({
        message: 'Provide `lines: [...]` or top-level speaker/emotion'
      });
    }
    if (scriptVersion) body.scriptVersion = scriptVersion;
    if (fileName) body.fileName = fileName;

    try {
      const url = config.POST_VOICE_RENDERING_DRY_RESOLVE;
      // eslint-disable-next-line no-console
      console.log(
        `[voiceRenderingSlice.dryResolveLines] POST ${url} ` +
        `bookId=${bookId} lineCount=${body.lines?.length ?? 1}`
      );
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.dryResolveLines] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

// ---------------------------------------------------------------------------
// Phase 4d Rendering Studio thunks
// ---------------------------------------------------------------------------

/**
 * GET /api/voice-rendering/:bookId/characters
 * Returns the full character roster for a book, each row enriched with the
 * count of finalized variations and (where present) the canonical neutral
 * voice preview. Used to populate the studio's left-rail character picker.
 */
export const fetchRenderCharacters = createAsyncThunk(
  'voiceRendering/fetchRenderCharacters',
  async (bookId, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_CHARACTERS, { bookId });
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchRenderCharacters] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { bookId, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchRenderCharacters] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/:bookId/grid/:characterUuid
 * Returns the 9-cell (3 emotions × 3 lines) eye-test grid for a character,
 * with last-rendered audio URLs (signed for ~1h) and metadata. Server picks
 * cells deterministically per the active script version.
 */
export const fetchCharacterGrid = createAsyncThunk(
  'voiceRendering/fetchCharacterGrid',
  async ({ bookId, characterUuid } = {}, { rejectWithValue }) => {
    if (!bookId || !characterUuid) {
      return rejectWithValue({ message: 'bookId and characterUuid required' });
    }
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_GRID, {
        bookId,
        characterUuid
      });
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchCharacterGrid] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { characterUuid, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchCharacterGrid] characterUuid=${characterUuid} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/:bookId/render-grid
 * Body: { characterUuid }
 * Server responds 202 + { jobId, total, snapshot } and starts a sequential
 * renderer in-process. Caller then polls `pollGridProgress(jobId)`.
 */
export const startGridRender = createAsyncThunk(
  'voiceRendering/startGridRender',
  async ({ bookId, characterUuid } = {}, { rejectWithValue }) => {
    if (!bookId || !characterUuid) {
      return rejectWithValue({ message: 'bookId and characterUuid required' });
    }
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_RENDER_GRID, { bookId });
      // eslint-disable-next-line no-console
      console.log(
        `[voiceRenderingSlice.startGridRender] POST ${url} characterUuid=${characterUuid}`
      );
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ characterUuid })
      });
      // 202 Accepted is success-with-payload; handle() treats >=400 as error
      // and parses JSON otherwise, so 202 falls through correctly.
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.startGridRender] characterUuid=${characterUuid} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/grid-progress/:jobId
 * Idempotent poll. Returns the current job snapshot:
 *   { jobId, status, total, completed, failed, currentCellId?, cells?: [...] }
 * The page hits this every ~2-3s while a render is in flight.
 */
export const pollGridProgress = createAsyncThunk(
  'voiceRendering/pollGridProgress',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_GRID_PROGRESS, { jobId });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.pollGridProgress] jobId=${jobId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/cell/:cellId/resign
 * Mints a fresh ~1h signed URL for an already-rendered cell so the user can
 * re-listen days later without forcing a full re-render. Cheap; no TTS hit.
 */
export const resignRenderedCell = createAsyncThunk(
  'voiceRendering/resignRenderedCell',
  async (cellId, { rejectWithValue }) => {
    if (!cellId) return rejectWithValue({ message: 'cellId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_CELL_RESIGN, { cellId });
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      const data = unwrap(payload);
      return { cellId, ...data };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.resignRenderedCell] cellId=${cellId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

// ---------------------------------------------------------------------------
// Phase 4d R3a — selector-driven render-unit thunks
// ---------------------------------------------------------------------------
//
// The six endpoints (`render-tree`, `preflight`, `render-unit`,
// `unit-progress/:jobId`, `rendered-lines`, `rendered-line/:lineUuid/url`)
// expose the selector-driven render pipeline that supersedes the per-character
// eye-test grid as the BC's primary render UI. Response shapes mirror the
// service surfaces documented in
//   - `PHASE_4D_RENDERING_STUDIO_R3_BATCH_3C_COMPLETE.md` §2 (services), and
//   - `PHASE_4D_RENDERING_STUDIO_R3_BATCH_3D_COMPLETE.md` §2 (endpoints).
//
// Special case: `startUnitRender` returns 409 with `{ error, preflight }` when
// `RenderJob.startUnitRender` throws a `PreflightBlockedError` (status RED).
// We catch that explicitly so the caller can read the verdict body and pop the
// blocker modal without a second round-trip to `/preflight`.

const handleWithPreflight409 = async (response, rejectWithValue) => {
  if (response.status === 409) {
    let body = {};
    try {
      body = await response.json();
    } catch (_) {
      body = {};
    }
    return rejectWithValue({
      code: body?.error?.code || 'PREFLIGHT_BLOCKED',
      message: body?.error?.message || 'Preflight blocked',
      preflight: body?.preflight || null,
      _preflightBlocked: true
    });
  }
  return handle(response, rejectWithValue);
};

/**
 * GET /api/voice-rendering/:bookId/render-tree?scriptVersion=…
 * Returns the full RenderUnitTree with counts + status pills. Caller passes
 * `scriptVersion` (uuid|version) or `fileName` to pin; default = latest.
 *
 * Response shape (per BATCH_3D §2):
 *   { bookId, scriptVersion, scriptLoadError, tree }
 */
export const fetchRenderTree = createAsyncThunk(
  'voiceRendering/fetchRenderTree',
  async ({ bookId, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const qs = new URLSearchParams();
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const url =
        buildUrl(config.GET_VOICE_RENDERING_RENDER_TREE, { bookId }) + suffix;
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchRenderTree] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { bookId, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchRenderTree] bookId=${bookId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  },
  {
    condition: ({ bookId, scriptVersion, fileName, force } = {}, { getState }) => {
      if (!bookId) return false;
      if (force) return true;
      const rt = getState().voiceRendering?.renderTree;
      if (rt?.loading?.[bookId]) return false;
      const cached = rt?.byBookId?.[bookId];
      if (!cached) return true;
      if (!isFresh(cached.lastFetched)) return true;
      // Version pin changed → force refetch.
      const cachedSV = cached.scriptVersion;
      if (scriptVersion &&
        cachedSV?.uuid !== scriptVersion &&
        cachedSV?.version !== String(scriptVersion)
      ) {
        return true;
      }
      if (fileName && cachedSV?.fileName !== fileName) return true;
      return false;
    }
  }
);

/**
 * GET /api/voice-rendering/:bookId/preflight?selector=…
 * Pure-ish traffic-light verdict for a selector. No TTS spend; no caching
 * beyond the slice's own `bySelector` map (cheap to refetch, and the BC may
 * change clones / variations between visits).
 *
 * Response shape (per BATCH_3D §2):
 *   { bookId, selector, scriptVersion, scriptLoadError, preflight }
 *   preflight: { status, lineCount, blockers, warnings, speakers, … }
 */
export const fetchRenderPreflight = createAsyncThunk(
  'voiceRendering/fetchRenderPreflight',
  async ({ bookId, selector, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    if (!selector) return rejectWithValue({ message: 'selector required' });
    try {
      const qs = new URLSearchParams({ selector });
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const url =
        buildUrl(config.GET_VOICE_RENDERING_PREFLIGHT, { bookId }) +
        '?' + qs.toString();
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchRenderPreflight] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { selector, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchRenderPreflight] selector=${selector} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/:bookId/render-unit
 * Body: { selector, force?, scriptVersion?, fileName? }
 * Server returns 202 + `{ job: <initial snapshot> }`. Caller then polls
 * `pollUnitProgress(jobId)`.
 *
 * If preflight is RED, server returns 409 with `{ error, preflight }`. The
 * thunk surfaces that on the rejected payload as
 * `{ code:'PREFLIGHT_BLOCKED', preflight, _preflightBlocked:true }` so the
 * reducer can stash it into `unitJob.preflightBlocked` for the modal.
 */
export const startUnitRender = createAsyncThunk(
  'voiceRendering/startUnitRender',
  async (
    { bookId, selector, lineIds, force, scriptVersion, fileName } = {},
    { rejectWithValue }
  ) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    if (!selector) return rejectWithValue({ message: 'selector required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_RENDER_UNIT, { bookId });
      const body = { selector };
      // Render only these lines. The server IMPLIES force for a subset, so the client never
      // has to remember to ask for it — and cannot forget.
      if (Array.isArray(lineIds) && lineIds.length) body.lineIds = lineIds;
      if (force) body.force = true;
      if (scriptVersion) body.scriptVersion = scriptVersion;
      if (fileName) body.fileName = fileName;
      // eslint-disable-next-line no-console
      console.log(
        `[voiceRenderingSlice.startUnitRender] POST ${url} ` +
        `selector=${selector} force=${!!force} lines=${(lineIds||[]).length||"all"}`
      );
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const payload = await handleWithPreflight409(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.startUnitRender] selector=${selector} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/unit-progress/:jobId
 * Idempotent poll. Returns the current job snapshot (defensive copy from
 * `RenderJob._jobs`). 404 once the in-memory store loses the job (process
 * restart in R3a; persisted in R4).
 *
 * Response shape (per BATCH_3D §2): unwrapped `{ job: {...} }` →
 *   { jobId, bookId, selector, status, totals, summary, preflight,
 *     rendered: [{ idx, lineId, status, signedUrl?, resolutionPath?,
 *                  reason?, error?, … }], elevenLabsMock, ... }
 */
export const pollUnitProgress = createAsyncThunk(
  'voiceRendering/pollUnitProgress',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_UNIT_PROGRESS, { jobId });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.pollUnitProgress] jobId=${jobId} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/:bookId/rendered-lines?selector=…
 * Returns the line-set table for a selector joined to the active
 * `:RenderedLine` per `lineId`. Rows may be unrendered (no uuid/s3Key) or
 * rendered (full audio metadata).
 *
 * Response shape (per BATCH_3D §2):
 *   { bookId, selector, scriptVersion, scriptLoadError, lineSet, rows, reason? }
 *   rows: [{ idx, lineId, speaker, emotion, text, chapterNumber, partTitle,
 *            type, isActive, uuid, s3Key, signedUrl, characterUuid,
 *            cloneVoiceId, resolutionPath, renderedAt, audioBytes,
 *            contentType, durationMs }]
 */
export const fetchRenderedLines = createAsyncThunk(
  'voiceRendering/fetchRenderedLines',
  async ({ bookId, selector, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    if (!selector) return rejectWithValue({ message: 'selector required' });
    try {
      const qs = new URLSearchParams({ selector });
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const url =
        buildUrl(config.GET_VOICE_RENDERING_RENDERED_LINES, { bookId }) +
        '?' + qs.toString();
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchRenderedLines] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { selector, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.fetchRenderedLines] selector=${selector} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/rendered-line/:lineUuid/url
 * Refresh the 1h pre-signed URL for an existing :RenderedLine. The client
 * `<audio>` element's `onError` handler hits this when the cached URL
 * expires (mirrors `resignRenderedCell` for the grid).
 *
 * Returns `{ lineUuid, signedUrl }` (unwrapped).
 */
export const resignRenderedLineUrl = createAsyncThunk(
  'voiceRendering/resignRenderedLineUrl',
  async (lineUuid, { rejectWithValue }) => {
    if (!lineUuid) return rejectWithValue({ message: 'lineUuid required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_RENDERED_LINE_URL, {
        lineUuid
      });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      const data = unwrap(payload);
      return { lineUuid, ...data };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[voiceRenderingSlice.resignRenderedLineUrl] lineUuid=${lineUuid} failed:`,
        e
      );
      return rejectWithValue({ message: e.message });
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

/**
 * GET /api/voice-rendering/:bookId/stitch-readiness?selector=book.body
 * R3c "Stitch Time" gate — is every in-scope line rendered? Returns the
 * traffic-light verdict. On-demand (Preflight Stitch button); refetches each
 * dispatch, guarded only against a concurrent in-flight request.
 */
export const fetchStitchReadiness = createAsyncThunk(
  'voiceRendering/fetchStitchReadiness',
  async ({ bookId, selector = 'book.body', scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const qs = new URLSearchParams({ selector });
      if (scriptVersion) qs.set('scriptVersion', scriptVersion);
      if (fileName) qs.set('fileName', fileName);
      const url =
        buildUrl(config.GET_VOICE_RENDERING_STITCH_READINESS, { bookId }) +
        '?' + qs.toString();
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.fetchStitchReadiness] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return { bookId, data: unwrap(payload) };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.fetchStitchReadiness] bookId=${bookId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  },
  {
    condition: ({ bookId } = {}, { getState }) => {
      if (!bookId) return false;
      if (getState().voiceRendering?.stitchReadiness?.loading?.[bookId]) return false;
      return true;
    }
  }
);

/**
 * Mirror of `handleWithPreflight409` for the stitch engine: a 409 means the
 * StitchReadiness gate is RED (some in-scope line unrendered). Surface the
 * `readiness` verdict on the rejected payload so the UI can explain the block
 * without a second round-trip.
 */
const handleWithStitch409 = async (response, rejectWithValue) => {
  if (response.status === 409) {
    let body = {};
    try {
      body = await response.json();
    } catch (_) {
      body = {};
    }
    return rejectWithValue({
      code: body?.error?.code || 'STITCH_BLOCKED',
      message: body?.error?.message || 'Stitch blocked',
      readiness: body?.readiness || null,
      _stitchBlocked: true
    });
  }
  return handle(response, rejectWithValue);
};

/**
 * POST /api/voice-rendering/:bookId/stitch
 * Body: { selector, force?, scriptVersion?, fileName? }
 * Assembles the rendered lines of a chunk into per-chapter chapter-{n}.raw.mp3
 * via ffmpeg concat. Server returns 202 + `{ job, readiness }`; caller polls
 * `pollStitchProgress(jobId)`. A RED readiness verdict returns 409 with
 * `{ readiness }` (unless force:true), surfaced as `_stitchBlocked`.
 */
export const startStitch = createAsyncThunk(
  'voiceRendering/startStitch',
  /**
   * ⛔ `redo` IS THE ONE THAT REBUILDS CURRENT CHAPTERS, and this thunk did not send it.
   *
   * `ChapterAssembler.startStitch` judges a chapter current by comparing its file's timestamp
   * against its LINES — so a change to the stitch itself (pause lengths, gap generation) leaves
   * every chapter looking up to date and the job reports *"nothing to do — 44 chapter(s) already
   * current"*. The controller has always read `req.body.redo`; nothing client-side set it.
   *
   * ⚠️ `force` is not even in `startStitch`'s signature — it was being sent and silently ignored.
   * Kept here because the server may grow one, but it does NOT rebuild anything today.
   */
  async ({ bookId, selector = 'book.body', force, redo, scriptVersion, fileName } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_STITCH, { bookId });
      const body = { selector };
      if (force) body.force = true;
      if (redo) body.redo = true;
      if (scriptVersion) body.scriptVersion = scriptVersion;
      if (fileName) body.fileName = fileName;
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.startStitch] POST ${url} selector=${selector} redo=${!!redo}`);
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const payload = await handleWithStitch409(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.startStitch] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/:bookId/export  { selector, unitLabel?, expiryDays? }
 *
 * Package the already-stitched chapters of a unit into one zip in S3 and return a signed
 * URL for sharing.
 *
 * ⚠️ The returned link is unguessable and expires, but it is NOT authenticated — anyone
 * holding it can download until it lapses. The UI must present it as a private link, never
 * as access-controlled.
 */
export const exportUnitZip = createAsyncThunk(
  'voiceRendering/exportUnitZip',
  async ({ bookId, selector = 'book.body', unitLabel, expiryDays } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_EXPORT, { bookId });
      const body = { selector };
      if (unitLabel) body.unitLabel = unitLabel;
      if (expiryDays) body.expiryDays = expiryDays;
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.exportUnitZip] POST ${url} selector=${selector}`);
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.exportUnitZip] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/:bookId/audio-lab?selector=…
 *
 * What audio exists for a unit, per chapter, per stage, with the measured ACX compliance of
 * anything mastered.
 *
 * DELIBERATELY NOT `pollStitchProgress`. That reads an IN-MEMORY job which dies with a server
 * restart, so a completed stitch looks like it never happened and re-stitching becomes the
 * only visible action. This reads the durable :RenderedChapter rows instead — the chapters
 * survive restarts even though the job does not.
 */
export const fetchAudioLab = createAsyncThunk(
  'voiceRendering/fetchAudioLab',
  /**
   * ⚠️ `book.full`, not `book.body` — the server matches the selector EXACTLY, so asking about
   * book.body for a book stitched as book.full reports zero chapters and reads as missing audio.
   * Measured on the live Mask: book.body → 0, book.full → 44. ⛔ Write paths keep their book.body
   * default deliberately; see the note in the controller's audioLabReport.
   */
  async ({ bookId, selector = 'book.full' } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const base = buildUrl(config.GET_VOICE_RENDERING_AUDIO_LAB, { bookId });
      const url = `${base}?selector=${encodeURIComponent(selector)}`;
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.fetchAudioLab] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/:bookId/delivery?selector=…
 *
 * The finished-book read: coverage, the ACX roll-up naming the chapter that came CLOSEST to failing,
 * and every artifact produced so far.
 *
 * ⭐ FREE AND RESTART-PROOF. It aggregates what mastering already stored rather than measuring, so it
 * answers even when every job registry is empty — which is exactly when the operator most needs it.
 */
export const fetchDeliveryReport = createAsyncThunk(
  'voiceRendering/fetchDeliveryReport',
  async ({ bookId, selector = 'book.full' } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const base = buildUrl(config.GET_VOICE_RENDERING_DELIVERY, { bookId });
      const res = await fetch(`${base}?selector=${encodeURIComponent(selector)}`, {
        method: 'GET', headers: authHeaders(),
      });
      return unwrap(await handle(res, rejectWithValue));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.fetchDeliveryReport] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/delivery-artifact/:uuid/link
 *
 * ⭐ Re-mint a download link for a zip already in storage — one API call, where re-exporting is twenty
 * minutes of ffmpeg and a 1.2 GB upload. Those were previously the same operation, which is how a
 * finished export came to be reachable only through the React state that held its URL.
 */
export const fetchArtifactLink = createAsyncThunk(
  'voiceRendering/fetchArtifactLink',
  async ({ uuid } = {}, { rejectWithValue }) => {
    if (!uuid) return rejectWithValue({ message: 'artifact uuid required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_DELIVERY_ARTIFACT_LINK, { uuid });
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      return unwrap(await handle(res, rejectWithValue));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.fetchArtifactLink] uuid=${uuid} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/delivery-artifact/:uuid/verify
 *
 * Check the zip against the chapter rows: names, archive ORDER and exact byte counts. Reads the zip's
 * own directory — measured at 64 KB of a 1,217 MB archive.
 *
 * ⚠️ Comes back `verified-by-size`, never a bare "verified". Identical length is not identical audio.
 */
export const verifyArtifact = createAsyncThunk(
  'voiceRendering/verifyArtifact',
  async ({ uuid, expectStage = 'mastered' } = {}, { rejectWithValue }) => {
    if (!uuid) return rejectWithValue({ message: 'artifact uuid required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_DELIVERY_ARTIFACT_VERIFY, { uuid });
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectStage }),
      });
      return unwrap(await handle(res, rejectWithValue));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.verifyArtifact] uuid=${uuid} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/:bookId/seam-reel
 *
 * Build one MP3 of every chapter join — 79 boundaries, ~23 minutes, against 14.77 hours of book.
 *
 * ⚠️ RUNS INLINE and takes minutes: it fetches the bytes around each boundary and re-encodes. Do not
 * add a timeout shorter than the work. ⛔ Refused with 409 while a stitch or master is in flight,
 * because a reel built from chapters being rewritten auditions joins that end up in no deliverable.
 */
export const buildSeamReel = createAsyncThunk(
  'voiceRendering/buildSeamReel',
  async ({ bookId, selector = 'book.full', stage = 'mastered' } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_SEAM_REEL, { bookId });
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ selector, stage }),
      });
      return unwrap(await handle(res, rejectWithValue));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.buildSeamReel] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/:bookId/master
 *
 * Master every raw chapter of a unit: gain to the preset's RMS target, limiter under the peak
 * ceiling, room tone, ID3 tags. Returns per-chapter before/after measurements.
 *
 * Runs INLINE — one ffmpeg pass per chapter, far cheaper than the stitch it follows. Expect
 * it to take a while on a long unit and do not add a timeout that is shorter than the work.
 */
export const masterUnit = createAsyncThunk(
  'voiceRendering/masterUnit',
  /**
   * ⛔ `force` AND `redo` ARE DIFFERENT FLAGS, and only `redo` re-masters current chapters.
   *
   *   force — override the self-halt after consecutive failures ("Run anyway" on a halted job)
   *   redo  — master again even though every chapter already looks current
   *
   * This thunk sent only `force`, so the Audio Lab's force control reported
   * *"nothing to do — 44 chapter(s) already mastered"* and did nothing: `startMastering` skips the
   * currency lookup on `redo`, never on `force`. Found 2026-10-01 trying to apply the room-tone fix.
   */
  async ({ bookId, selector = 'book.body', presetKey, unitLabel, author, force, redo } = {}, { rejectWithValue }) => {
    if (!bookId) return rejectWithValue({ message: 'bookId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_MASTER, { bookId });
      const body = { selector };
      if (presetKey) body.presetKey = presetKey;
      if (unitLabel) body.unitLabel = unitLabel;
      if (author) body.author = author;
      if (force) body.force = true;
      if (redo) body.redo = true;
      // eslint-disable-next-line no-console
      console.log(`[voiceRenderingSlice.masterUnit] POST ${url} selector=${selector} preset=${presetKey}`);
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.masterUnit] selector=${selector} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/master-progress/:jobId
 *
 * Poll a running mastering job. Per-chapter verdicts appear AS THEY LAND, so a systematic
 * failure shows after one chapter instead of after all nineteen — which is what happened when
 * a cancelled gain made every chapter fail and the run still went to completion first.
 *
 * 404 once the registry releases the job; that is not lost work, the chapters are durable.
 */
export const pollMasteringProgress = createAsyncThunk(
  'voiceRendering/pollMasteringProgress',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_MASTER_PROGRESS, { jobId });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.pollMasteringProgress] jobId=${jobId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * POST /api/voice-rendering/master/:jobId/abort
 *
 * Stop a running mastering job. Chapters already mastered are KEPT — each persists as it
 * completes, so aborting at chapter 12 leaves eleven usable mastered chapters.
 */
export const abortMastering = createAsyncThunk(
  'voiceRendering/abortMastering',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_MASTER_ABORT, { jobId });
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.abortMastering] jobId=${jobId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/stitch-progress/:jobId
 * Idempotent poll of the in-memory stitch job snapshot (per-chapter status,
 * rawS3Key, signedUrl, durationMs). 404 once the store loses the job.
 */
/**
 * Ask a running stitch to stop after its current chapter.
 *
 * Cooperative: the chapter in flight finishes and is persisted, so a stopped stitch is
 * PARTIAL rather than wasted. Reloading the page never did this — it stopped the browser
 * watching while the job ran on to the end.
 */
/**
 * Stop a RENDER in flight.
 *
 * ⭐ WHY IT EXISTS: the operator was watching a render and spotted a line that needed joining.
 * The stitch and the mastering stages could both be stopped; the render — the stage that spends
 * ElevenLabs characters and runs longest — could not.
 *
 * ⚠️ Cooperative, like the stitch: the line in flight finishes and is persisted. Every line before
 * the stop is finished work, so nothing needs cleaning up and no recovery is required.
 */
export const stopUnitRender = createAsyncThunk(
  'voiceRendering/stopUnitRender',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_UNIT_RENDER_STOP, { jobId });
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.stopUnitRender] jobId=${jobId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

export const stopStitch = createAsyncThunk(
  'voiceRendering/stopStitch',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.POST_VOICE_RENDERING_STITCH_STOP, { jobId });
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.stopStitch] jobId=${jobId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

export const pollStitchProgress = createAsyncThunk(
  'voiceRendering/pollStitchProgress',
  async (jobId, { rejectWithValue }) => {
    if (!jobId) return rejectWithValue({ message: 'jobId required' });
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_STITCH_PROGRESS, { jobId });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      return unwrap(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.pollStitchProgress] jobId=${jobId} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

/**
 * GET /api/voice-rendering/rendered-chapter/:chapterUuid/url
 * Refresh the 1h signed URL for a stitched chapter so the operator can
 * re-listen after expiry. Returns `{ chapterUuid, signedUrl }`.
 */
export const resignRenderedChapterUrl = createAsyncThunk(
  'voiceRendering/resignRenderedChapterUrl',
  async (chapterUuid, { rejectWithValue }) => {
    if (!chapterUuid) return rejectWithValue({ message: 'chapterUuid required' });
    // A non-string arg used to sail past the check above and stringify into the path as
    // "[object Object]", surfacing as a bare 404 that says nothing about the real mistake.
    // Fail here, naming it.
    if (typeof chapterUuid !== 'string') {
      return rejectWithValue({
        message: `resignRenderedChapterUrl expects the uuid string, received ${typeof chapterUuid}`
      });
    }
    try {
      const url = buildUrl(config.GET_VOICE_RENDERING_RENDERED_CHAPTER_URL, { chapterUuid });
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      const data = unwrap(payload);
      return { chapterUuid, ...data };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceRenderingSlice.resignRenderedChapterUrl] chapterUuid=${chapterUuid} failed:`, e);
      return rejectWithValue({ message: e.message });
    }
  }
);

const initialState = {
  byBook: {},
  // Phase 4d Rendering Studio additions ------------------------------------
  // Keyed independently from `byBook` because characters are book-scoped
  // (`renderCharacters`) but a grid lookup is character-scoped (multiple
  // grids could be cached simultaneously across books) and a job is global.
  renderCharacters: {
    byBookId: {},   // bookId → { items: [...], scriptVersion, snapshot }
    loading: {},    // bookId → bool
    error: {}       // bookId → string|null
  },
  characterGrid: {
    byCharacterUuid: {},   // characterUuid → { cells: [...], emotions, lineIndices, ... }
    loading: {},           // characterUuid → bool
    error: {}              // characterUuid → string|null
  },
  renderJob: {
    current: null,  // { jobId, characterUuid, status, total, completed, failed, currentCellId?, cells?: [] }
    loading: false, // start/poll fetch in flight
    error: null
  },
  // Phase 4d R3a — selector-driven render-unit state -----------------------
  // Tree is per-bookId (one active book at a time); preflight + renderedLines
  // are per-selector (cheap to refetch, the BC may flip between siblings);
  // the unit-job is a singleton (mirrors `renderJob.current`) plus a
  // `preflightBlocked` slot that captures the 409 verdict body.
  renderTree: {
    byBookId: {},   // bookId → { tree, scriptVersion, scriptLoadError, lastFetched }
    loading: {},    // bookId → bool
    error: {}       // bookId → string|null
  },
  preflight: {
    bySelector: {}, // selector → { bookId, preflight, scriptVersion, scriptLoadError, receivedAt }
    loading: {},    // selector → bool
    error: {}       // selector → string|null
  },
  unitJob: {
    current: null,  // { jobId, bookId, selector, status, totals, summary, preflight, rendered:[…] }
    loading: false, // start fetch in flight
    error: null,
    preflightBlocked: null  // { selector, preflight }  — set on 409, cleared on next start
  },
  renderedLines: {
    bySelector: {}, // selector → { bookId, lineSet, rows, reason, scriptVersion, scriptLoadError, receivedAt }
    loading: {},    // selector → bool
    error: {}       // selector → string|null
  },
  // Phase 4d R3c — stitch-readiness gate, keyed per-bookId (Stitch Book is book-scoped).
  stitchReadiness: {
    byBookId: {},   // bookId → { readiness, selector, scriptLoadError, receivedAt }
    loading: {},    // bookId → bool
    error: {}       // bookId → string|null
  },
  // Phase 4d R3c — stitch engine job (singleton, mirrors unitJob). `blocked`
  // captures a 409 RED-readiness verdict so the UI can explain why the stitch
  // didn't start.
  stitchJob: {
    current: null,  // { jobId, bookId, selector, status, totals, chapters:[…] }
    loading: false, // start fetch in flight
    error: null,
    blocked: null   // { selector, readiness } — set on 409, cleared on next start
  }
};


const slice = createSlice({
  name: 'voiceRendering',
  initialState,
  reducers: {
    /**
     * Wipe the `resolve` payload for a book (e.g. on unmount / book change).
     */
    clearDryResolve(state, action) {
      const payload = action.payload;
      const bookId =
        typeof payload === 'string' ? payload : payload && payload.bookId;
      if (!bookId || !state.byBook[bookId]) return;
      state.byBook[bookId].resolve = {
        results: [],
        snapshot: null,
        scriptVersion: null,
        scriptLoaded: false,
        scriptLoadError: null,
        requestedLineCount: 0,
        receivedAt: null
      };
      state.byBook[bookId].error.resolve = null;
    },
    clearVoiceRenderingError(state, action) {
      const { bookId, scope = 'all' } = action.payload || {};
      if (!bookId || !state.byBook[bookId]) return;
      if (scope === 'all' || scope === 'snapshot') {
        state.byBook[bookId].error.snapshot = null;
      }
      if (scope === 'all' || scope === 'resolve') {
        state.byBook[bookId].error.resolve = null;
      }
    },
    /**
     * Reset the in-flight render job. Used on:
     *   - Rendering Studio page unmount
     *   - User picks a different character (we never want to keep polling
     *     a job whose results no longer correspond to the visible grid)
     *   - User dismisses the "all done!" banner.
     */
    clearRenderJob(state) {
      state.renderJob.current = null;
      state.renderJob.loading = false;
      state.renderJob.error = null;
    },
    /**
     * Wipe the cached selector tree for a book (book change / unmount).
     * Accepts either `bookId` or `{ bookId }`.
     */
    clearRenderTree(state, action) {
      const payload = action.payload;
      const bookId =
        typeof payload === 'string' ? payload : payload && payload.bookId;
      if (!bookId) return;
      delete state.renderTree.byBookId[bookId];
      delete state.renderTree.loading[bookId];
      delete state.renderTree.error[bookId];
    },
    /**
     * Wipe one or all preflight verdicts. Pass `{ selector }` to drop a
     * single entry, or omit / pass nothing to clear the whole map.
     */
    clearPreflight(state, action) {
      const selector = action.payload?.selector;
      if (!selector) {
        state.preflight.bySelector = {};
        state.preflight.loading = {};
        state.preflight.error = {};
        return;
      }
      delete state.preflight.bySelector[selector];
      delete state.preflight.loading[selector];
      delete state.preflight.error[selector];
    },
    /**
     * Reset the in-flight unit-render job (page unmount, selector switch,
     * banner dismissal). Also clears a pending `preflightBlocked` modal.
     */
    clearUnitJob(state) {
      state.unitJob.current = null;
      state.unitJob.loading = false;
      state.unitJob.error = null;
      state.unitJob.preflightBlocked = null;
    },
    /**
     * Wipe one or all rendered-line tables. Same shape as `clearPreflight`.
     */
    clearRenderedLines(state, action) {
      const selector = action.payload?.selector;
      if (!selector) {
        state.renderedLines.bySelector = {};
        state.renderedLines.loading = {};
        state.renderedLines.error = {};
        return;
      }
      delete state.renderedLines.bySelector[selector];
      delete state.renderedLines.loading[selector];
      delete state.renderedLines.error[selector];
    },
    /**
     * Reset the in-flight stitch job (page unmount, book/scope switch, banner
     * dismissal). Also clears a pending `blocked` (409 readiness) verdict.
     */
    clearStitchJob(state) {
      state.stitchJob.current = null;
      state.stitchJob.loading = false;
      state.stitchJob.error = null;
      state.stitchJob.blocked = null;
    }
  },

  extraReducers: (builder) => {
    builder
      // fetchBookRenderSnapshot ----------------------------------------
      .addCase(fetchBookRenderSnapshot.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.snapshot = true;
        entry.error.snapshot = null;
      })
      .addCase(fetchBookRenderSnapshot.fulfilled, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.snapshot = false;
        entry.error.snapshot = null;
        entry.snapshot = action.payload?.snapshot || null;
        entry.scriptVersion = action.payload?.scriptVersion || null;
        entry.scriptLoaded = !!action.payload?.scriptLoaded;
        entry.scriptLoadError = action.payload?.scriptLoadError || null;
        entry.lastFetched.snapshot = new Date().toISOString();
      })
      .addCase(fetchBookRenderSnapshot.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.snapshot = false;
        entry.error.snapshot =
          action.payload?.message ||
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load render snapshot';
      })

      // dryResolveLines ------------------------------------------------
      .addCase(dryResolveLines.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.resolve = true;
        entry.error.resolve = null;
      })
      .addCase(dryResolveLines.fulfilled, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        const payload = action.payload || {};
        entry.loading.resolve = false;
        entry.error.resolve = null;
        entry.resolve = {
          results: Array.isArray(payload.results) ? payload.results : [],
          snapshot: payload.snapshot || null,
          scriptVersion: payload.scriptVersion || null,
          scriptLoaded: !!payload.scriptLoaded,
          scriptLoadError: payload.scriptLoadError || null,
          requestedLineCount: Array.isArray(payload.results) ? payload.results.length : 0,
          receivedAt: new Date().toISOString()
        };
        // Snapshot piggybacks on dry-resolve responses; cache it for the
        // header without forcing a separate GET.
        if (payload.snapshot) entry.snapshot = payload.snapshot;
        if (payload.scriptVersion) entry.scriptVersion = payload.scriptVersion;
        entry.scriptLoaded = !!payload.scriptLoaded;
        entry.scriptLoadError = payload.scriptLoadError || null;
      })
      .addCase(dryResolveLines.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        const entry = ensureBookEntry(state, bookId);
        entry.loading.resolve = false;
        entry.error.resolve =
          action.payload?.message ||
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to dry-resolve lines';
      })

      // ----------------------------------------------------------------
      // Phase 4d Rendering Studio
      // ----------------------------------------------------------------
      // fetchRenderCharacters
      .addCase(fetchRenderCharacters.pending, (state, action) => {
        const bookId = action.meta.arg;
        if (!bookId) return;
        state.renderCharacters.loading[bookId] = true;
        state.renderCharacters.error[bookId] = null;
      })
      .addCase(fetchRenderCharacters.fulfilled, (state, action) => {
        const { bookId, data } = action.payload || {};
        if (!bookId) return;
        state.renderCharacters.loading[bookId] = false;
        state.renderCharacters.error[bookId] = null;
        state.renderCharacters.byBookId[bookId] = data || { items: [] };
      })
      .addCase(fetchRenderCharacters.rejected, (state, action) => {
        const bookId = action.meta.arg;
        if (!bookId) return;
        state.renderCharacters.loading[bookId] = false;
        state.renderCharacters.error[bookId] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load render characters';
      })

      // fetchCharacterGrid
      .addCase(fetchCharacterGrid.pending, (state, action) => {
        const { characterUuid } = action.meta.arg || {};
        if (!characterUuid) return;
        state.characterGrid.loading[characterUuid] = true;
        state.characterGrid.error[characterUuid] = null;
      })
      .addCase(fetchCharacterGrid.fulfilled, (state, action) => {
        const { characterUuid, data } = action.payload || {};
        if (!characterUuid) return;
        state.characterGrid.loading[characterUuid] = false;
        state.characterGrid.error[characterUuid] = null;
        state.characterGrid.byCharacterUuid[characterUuid] = data || null;
      })
      .addCase(fetchCharacterGrid.rejected, (state, action) => {
        const { characterUuid } = action.meta.arg || {};
        if (!characterUuid) return;
        state.characterGrid.loading[characterUuid] = false;
        state.characterGrid.error[characterUuid] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load character grid';
      })

      // startGridRender — server returns 202 + { job: {...} }. We unwrap
      // `job` here so the slice surface matches `renderJob.current`'s shape.
      // The `job.cells` array is keyed by `characterSampleId` (the CSS uuid),
      // and each cell's persisted RenderedGridCell uuid lives at
      // `lastRendered.cellId` — see API contract §2.3.
      .addCase(startGridRender.pending, (state) => {
        state.renderJob.loading = true;
        state.renderJob.error = null;
      })
      .addCase(startGridRender.fulfilled, (state, action) => {
        state.renderJob.loading = false;
        state.renderJob.error = null;
        const job = action.payload?.job || null;
        state.renderJob.current = job;
      })
      .addCase(startGridRender.rejected, (state, action) => {
        state.renderJob.loading = false;
        state.renderJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Failed to start grid render';
      })

      // pollGridProgress — replaces `renderJob.current` with the latest
      // job snapshot (server is the source of truth) and mirrors per-cell
      // status/audio updates into the cached grid so the table re-renders
      // without a separate fetchCharacterGrid round-trip.
      .addCase(pollGridProgress.fulfilled, (state, action) => {
        const job = action.payload?.job || null;
        if (!job) return;
        if (
          state.renderJob.current?.jobId &&
          job.jobId !== state.renderJob.current.jobId
        ) {
          // Stale poll from a job we replaced; ignore.
          return;
        }
        state.renderJob.current = job;

        // Mirror updated cell snapshots into the cached grid. Cells in both
        // the grid response and the job snapshot are keyed by
        // `characterSampleId` (the CSS uuid).
        const charUuid = job.characterUuid;
        if (charUuid && state.characterGrid.byCharacterUuid[charUuid]) {
          const grid = state.characterGrid.byCharacterUuid[charUuid];
          if (Array.isArray(grid.cells) && Array.isArray(job.cells)) {
            const bySampleId = new Map(
              job.cells.map((c) => [c.characterSampleId, c])
            );
            grid.cells = grid.cells.map((c) => {
              const upd = bySampleId.get(c.characterSampleId);
              if (!upd) return c;
              // Promote a successful render into `lastRendered` so the
              // table's audio player can pick it up immediately.
              const next = { ...c, ...upd };
              if (upd.status === 'success' && upd.renderedUrl) {
                next.lastRendered = {
                  cellId: upd.cellId || c.lastRendered?.cellId || null,
                  s3Key: upd.renderedS3Key || c.lastRendered?.s3Key || null,
                  signedUrl: upd.renderedUrl,
                  durationMs:
                    upd.durationMs ?? c.lastRendered?.durationMs ?? null,
                  audioBytes:
                    upd.audioBytes ?? c.lastRendered?.audioBytes ?? null,
                  contentType:
                    upd.contentType || c.lastRendered?.contentType || null,
                  updatedAt: upd.finishedAt || new Date().toISOString()
                };
              }
              return next;
            });
          }
        }
      })
      .addCase(pollGridProgress.rejected, (state, action) => {
        // Don't throw away the in-flight job on a transient poll error.
        // Surface the message so the page can show a non-fatal banner.
        state.renderJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Progress poll failed';
      })

      // resignRenderedCell — server returns { cellId, signedUrl }, where
      // `cellId` is the RenderedGridCell uuid (lives at
      // `cells[].lastRendered.cellId`). Patch the matching grid cell's
      // `lastRendered.signedUrl` in every cached grid.
      .addCase(resignRenderedCell.fulfilled, (state, action) => {
        const { cellId, signedUrl } = action.payload || {};
        if (!cellId || !signedUrl) return;
        const patchGridCells = (cells) => {
          if (!Array.isArray(cells)) return cells;
          return cells.map((c) =>
            c.lastRendered?.cellId === cellId
              ? {
                ...c,
                lastRendered: { ...c.lastRendered, signedUrl }
              }
              : c
          );
        };
        Object.values(state.characterGrid.byCharacterUuid).forEach((grid) => {
          if (grid && Array.isArray(grid.cells)) {
            grid.cells = patchGridCells(grid.cells);
          }
        });
        // The job-snapshot cells use `cellId` directly + `renderedUrl`,
        // so patch that shape too.
        if (state.renderJob.current?.cells) {
          state.renderJob.current.cells = state.renderJob.current.cells.map(
            (c) => (c.cellId === cellId ? { ...c, renderedUrl: signedUrl } : c)
          );
        }
      })

      // ----------------------------------------------------------------
      // Phase 4d R3a — selector-driven render-unit pipeline
      // ----------------------------------------------------------------

      // fetchRenderTree — caches the tree per-bookId with TTL gate above.
      .addCase(fetchRenderTree.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        state.renderTree.loading[bookId] = true;
        state.renderTree.error[bookId] = null;
      })
      .addCase(fetchRenderTree.fulfilled, (state, action) => {
        const { bookId, data } = action.payload || {};
        if (!bookId) return;
        state.renderTree.loading[bookId] = false;
        state.renderTree.error[bookId] = null;
        state.renderTree.byBookId[bookId] = {
          tree: data?.tree || null,
          scriptVersion: data?.scriptVersion || null,
          scriptLoadError: data?.scriptLoadError || null,
          // Correctable text still outstanding, counted at ingest. Rides with the tree so the
          // Studio can warn before a long render instead of after one.
          review: data?.review || null,
          lastFetched: new Date().toISOString()
        };
      })
      .addCase(fetchRenderTree.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        state.renderTree.loading[bookId] = false;
        state.renderTree.error[bookId] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load render tree';
      })

      // fetchRenderPreflight — by-selector cache; no TTL guard.
      .addCase(fetchRenderPreflight.pending, (state, action) => {
        const { selector } = action.meta.arg || {};
        if (!selector) return;
        state.preflight.loading[selector] = true;
        state.preflight.error[selector] = null;
      })
      .addCase(fetchRenderPreflight.fulfilled, (state, action) => {
        const { selector, data } = action.payload || {};
        if (!selector) return;
        state.preflight.loading[selector] = false;
        state.preflight.error[selector] = null;
        state.preflight.bySelector[selector] = {
          bookId: data?.bookId || null,
          preflight: data?.preflight || null,
          scriptVersion: data?.scriptVersion || null,
          scriptLoadError: data?.scriptLoadError || null,
          receivedAt: new Date().toISOString()
        };
      })
      .addCase(fetchRenderPreflight.rejected, (state, action) => {
        const { selector } = action.meta.arg || {};
        if (!selector) return;
        state.preflight.loading[selector] = false;
        state.preflight.error[selector] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load preflight';
      })

      // startUnitRender — server returns 202 + { job }. On 409 we capture
      // the preflight verdict body into `unitJob.preflightBlocked` so the UI
      // can pop the blocker modal without a follow-up /preflight call.
      .addCase(startUnitRender.pending, (state) => {
        state.unitJob.loading = true;
        state.unitJob.error = null;
        state.unitJob.preflightBlocked = null;
      })
      .addCase(startUnitRender.fulfilled, (state, action) => {
        state.unitJob.loading = false;
        state.unitJob.error = null;
        state.unitJob.preflightBlocked = null;
        state.unitJob.current = action.payload?.job || null;
      })
      .addCase(startUnitRender.rejected, (state, action) => {
        state.unitJob.loading = false;
        if (action.payload?._preflightBlocked) {
          // 409 from the server — carrying the verdict body. Don't blow
          // away `current` (a previous job's results may still be valid),
          // and don't surface as a generic error.
          state.unitJob.error = null;
          state.unitJob.preflightBlocked = {
            selector: action.meta.arg?.selector || null,
            preflight: action.payload.preflight || null
          };
          return;
        }
        state.unitJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Failed to start unit render';
      })

      // pollUnitProgress — replaces `unitJob.current` with the latest
      // snapshot (server is the source of truth). Stale polls are ignored
      // (jobId mismatch). Mirrors per-line updates into the cached
      // renderedLines table for the same selector so the line-set table
      // re-renders without a separate fetchRenderedLines round-trip.
      .addCase(pollUnitProgress.fulfilled, (state, action) => {
        const job = action.payload?.job || null;
        if (!job) return;
        if (
          state.unitJob.current?.jobId &&
          job.jobId !== state.unitJob.current.jobId
        ) {
          // Stale poll from a job we replaced; ignore.
          return;
        }
        state.unitJob.current = job;
        state.unitJob.error = null;

        // Mirror rendered-line updates into the cached renderedLines table.
        // Both shapes key by `lineId`; we patch fields the line-set table
        // displays (audio URL, status, resolution path).
        const sel = job.selector;
        const cached = sel && state.renderedLines.bySelector[sel];
        if (
          cached &&
          Array.isArray(cached.rows) &&
          Array.isArray(job.rendered)
        ) {
          const byLineId = new Map(
            job.rendered.map((r) => [r.lineId, r])
          );
          cached.rows = cached.rows.map((row) => {
            const upd = byLineId.get(row.lineId);
            if (!upd) return row;
            const next = { ...row };
            // Always carry the latest job-status and reason/error.
            next.lastJobStatus = upd.status || null;
            if (upd.reason) next.lastJobReason = upd.reason;
            if (upd.error) next.lastJobError = upd.error;
            // SUCCESS / CACHED have a fresh signedUrl + uuid + s3Key.
            if (
              (upd.status === 'success' || upd.status === 'cached') &&
              upd.signedUrl
            ) {
              next.signedUrl = upd.signedUrl;
              if (upd.uuid) next.uuid = upd.uuid;
              if (upd.s3Key) next.s3Key = upd.s3Key;
              if (upd.resolutionPath) next.resolutionPath = upd.resolutionPath;
              if (upd.characterUuid) next.characterUuid = upd.characterUuid;
              if (upd.cloneVoiceId) next.cloneVoiceId = upd.cloneVoiceId;
              if (upd.durationMs != null) next.durationMs = upd.durationMs;
              if (upd.audioBytes != null) next.audioBytes = upd.audioBytes;
              if (upd.contentType) next.contentType = upd.contentType;
              if (upd.renderedAt) next.renderedAt = upd.renderedAt;
              next.isActive = true;
            }
            return next;
          });
        }
      })
      .addCase(pollUnitProgress.rejected, (state, action) => {
        // Don't drop the in-flight job on a transient poll error; surface
        // the message for a non-fatal banner. Mirrors pollGridProgress.
        state.unitJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Unit progress poll failed';
      })

      // fetchRenderedLines — straight cache into bySelector.
      .addCase(fetchRenderedLines.pending, (state, action) => {
        const { selector } = action.meta.arg || {};
        if (!selector) return;
        state.renderedLines.loading[selector] = true;
        state.renderedLines.error[selector] = null;
      })
      .addCase(fetchRenderedLines.fulfilled, (state, action) => {
        const { selector, data } = action.payload || {};
        if (!selector) return;
        state.renderedLines.loading[selector] = false;
        state.renderedLines.error[selector] = null;
        state.renderedLines.bySelector[selector] = {
          bookId: data?.bookId || null,
          lineSet: Array.isArray(data?.lineSet) ? data.lineSet : [],
          rows: Array.isArray(data?.rows) ? data.rows : [],
          reason: data?.reason || null,
          scriptVersion: data?.scriptVersion || null,
          scriptLoadError: data?.scriptLoadError || null,
          receivedAt: new Date().toISOString()
        };
      })
      .addCase(fetchRenderedLines.rejected, (state, action) => {
        const { selector } = action.meta.arg || {};
        if (!selector) return;
        state.renderedLines.loading[selector] = false;
        state.renderedLines.error[selector] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to load rendered lines';
      })

      // fetchStitchReadiness — by-bookId cache (whole-book stitch gate).
      .addCase(fetchStitchReadiness.pending, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        state.stitchReadiness.loading[bookId] = true;
        state.stitchReadiness.error[bookId] = null;
      })
      .addCase(fetchStitchReadiness.fulfilled, (state, action) => {
        const { bookId, data } = action.payload || {};
        if (!bookId) return;
        state.stitchReadiness.loading[bookId] = false;
        state.stitchReadiness.error[bookId] = null;
        state.stitchReadiness.byBookId[bookId] = {
          readiness: data?.readiness || null,
          selector: data?.selector || null,
          scriptLoadError: data?.scriptLoadError || null,
          receivedAt: new Date().toISOString()
        };
      })
      .addCase(fetchStitchReadiness.rejected, (state, action) => {
        const { bookId } = action.meta.arg || {};
        if (!bookId) return;
        state.stitchReadiness.loading[bookId] = false;
        state.stitchReadiness.error[bookId] =
          action.payload?.message ||
          action.error?.message ||
          'Failed to check stitch readiness';
      })

      // startStitch — 202 + { job }. On 409 (RED readiness) capture the
      // verdict into `stitchJob.blocked` so the UI can explain the block.
      .addCase(startStitch.pending, (state) => {
        state.stitchJob.loading = true;
        state.stitchJob.error = null;
        state.stitchJob.blocked = null;
      })
      .addCase(startStitch.fulfilled, (state, action) => {
        state.stitchJob.loading = false;
        state.stitchJob.error = null;
        state.stitchJob.blocked = null;
        state.stitchJob.current = action.payload?.job || null;
      })
      .addCase(startStitch.rejected, (state, action) => {
        state.stitchJob.loading = false;
        if (action.payload?._stitchBlocked) {
          state.stitchJob.error = null;
          state.stitchJob.blocked = {
            selector: action.meta.arg?.selector || null,
            readiness: action.payload.readiness || null
          };
          return;
        }
        state.stitchJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Failed to start stitch';
      })

      // pollStitchProgress — replace `stitchJob.current` with the latest
      // snapshot (server is source of truth); ignore stale polls (jobId
      // mismatch). Mirrors pollUnitProgress.
      .addCase(pollStitchProgress.fulfilled, (state, action) => {
        const job = action.payload?.job || null;
        if (!job) return;
        if (
          state.stitchJob.current?.jobId &&
          job.jobId !== state.stitchJob.current.jobId
        ) {
          return;
        }
        state.stitchJob.current = job;
        state.stitchJob.error = null;
      })
      .addCase(pollStitchProgress.rejected, (state, action) => {
        state.stitchJob.error =
          action.payload?.message ||
          action.error?.message ||
          'Stitch progress poll failed';
      })

      // resignRenderedChapterUrl — patch the fresh signedUrl onto the matching
      // chapter in the active stitch job.
      .addCase(resignRenderedChapterUrl.fulfilled, (state, action) => {
        const { chapterUuid, signedUrl } = action.payload || {};
        if (!chapterUuid || !signedUrl) return;
        if (Array.isArray(state.stitchJob.current?.chapters)) {
          state.stitchJob.current.chapters =
            state.stitchJob.current.chapters.map((c) =>
              c.renderedChapterUuid === chapterUuid ? { ...c, signedUrl } : c
            );
        }
      })

      // resignRenderedLineUrl — patch matching `signedUrl` everywhere it
      // could be cached: across every renderedLines.bySelector[*].rows and
      // the active unitJob.current.rendered[*]. Mirrors the
      // resignRenderedCell.fulfilled patch-everywhere idiom.
      .addCase(resignRenderedLineUrl.fulfilled, (state, action) => {
        const { lineUuid, signedUrl } = action.payload || {};
        if (!lineUuid || !signedUrl) return;
        Object.values(state.renderedLines.bySelector).forEach((entry) => {
          if (entry && Array.isArray(entry.rows)) {
            entry.rows = entry.rows.map((row) =>
              row.uuid === lineUuid ? { ...row, signedUrl } : row
            );
          }
        });
        if (Array.isArray(state.unitJob.current?.rendered)) {
          state.unitJob.current.rendered =
            state.unitJob.current.rendered.map((r) =>
              r.uuid === lineUuid || r.lineUuid === lineUuid
                ? { ...r, signedUrl }
                : r
            );
        }
      });

  }
});

export const {
  clearDryResolve,
  clearVoiceRenderingError,
  clearRenderJob,
  clearRenderTree,
  clearPreflight,
  clearUnitJob,
  clearRenderedLines,
  clearStitchJob
} = slice.actions;


// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const root = (state) => state.voiceRendering || initialState;
const bookEntry = (state, bookId) =>
  (bookId ? root(state).byBook[bookId] : null);

// Stable empty-array reference for "no rows/lineSet yet" fallbacks. Returning a
// fresh `[]` per call makes a selector yield a new reference for identical
// state, tripping React-Redux's "returned a different result when called with
// the same parameters" warning and forcing needless rerenders.
const EMPTY_ARRAY = Object.freeze([]);

export const selectBookRenderSnapshot = (state, bookId) =>
  bookEntry(state, bookId)?.snapshot || null;

export const selectBookRenderScriptVersion = (state, bookId) =>
  bookEntry(state, bookId)?.scriptVersion || null;

export const selectBookRenderScriptLoaded = (state, bookId) =>
  !!bookEntry(state, bookId)?.scriptLoaded;

export const selectBookDryResolve = (state, bookId) =>
  bookEntry(state, bookId)?.resolve || null;

export const selectBookRenderLoading = (state, bookId) => {
  const e = bookEntry(state, bookId);
  return {
    snapshot: !!e?.loading?.snapshot,
    resolve: !!e?.loading?.resolve
  };
};

export const selectBookRenderError = (state, bookId) => {
  const e = bookEntry(state, bookId);
  return {
    snapshot: e?.error?.snapshot || null,
    resolve: e?.error?.resolve || null
  };
};

// ---- Phase 4d Rendering Studio selectors -----------------------------------

export const selectRenderCharacters = (state, bookId) =>
  (bookId && root(state).renderCharacters.byBookId[bookId]) || null;

export const selectRenderCharactersLoading = (state, bookId) =>
  !!(bookId && root(state).renderCharacters.loading[bookId]);

export const selectRenderCharactersError = (state, bookId) =>
  (bookId && root(state).renderCharacters.error[bookId]) || null;

export const selectCharacterGrid = (state, characterUuid) =>
  (characterUuid &&
    root(state).characterGrid.byCharacterUuid[characterUuid]) ||
  null;

export const selectCharacterGridLoading = (state, characterUuid) =>
  !!(characterUuid && root(state).characterGrid.loading[characterUuid]);

export const selectCharacterGridError = (state, characterUuid) =>
  (characterUuid && root(state).characterGrid.error[characterUuid]) || null;

export const selectRenderJob = (state) => root(state).renderJob.current;
export const selectRenderJobLoading = (state) =>
  !!root(state).renderJob.loading;
export const selectRenderJobError = (state) =>
  root(state).renderJob.error || null;

/**
 * Convenience: true while there is a job in flight (status not in a terminal
 * bucket). Pages use this to drive the polling timer.
 */
export const selectRenderJobInFlight = (state) => {
  const job = root(state).renderJob.current;
  if (!job) return false;
  // Server emits 'running' | 'complete' | 'failed'. Anything not in the
  // terminal set is in flight (including 'pending' if a future server adds
  // that, hence the conservative default).
  const terminal = ['complete', 'failed', 'cancelled'];
  return !terminal.includes(job.status);
};

// ---- Phase 4d R3a — selector-driven render-unit selectors -----------------

export const selectRenderTreeEntry = (state, bookId) =>
  (bookId && root(state).renderTree.byBookId[bookId]) || null;

export const selectRenderTree = (state, bookId) =>
  selectRenderTreeEntry(state, bookId)?.tree || null;

export const selectRenderTreeScriptVersion = (state, bookId) =>
  selectRenderTreeEntry(state, bookId)?.scriptVersion || null;

/** Text corrections still outstanding for this book — {textItems, textOccurrences, hyphens}. */
export const selectRenderTreeReview = (state, bookId) =>
  selectRenderTreeEntry(state, bookId)?.review || null;

export const selectRenderTreeLoading = (state, bookId) =>
  !!(bookId && root(state).renderTree.loading[bookId]);

export const selectRenderTreeError = (state, bookId) =>
  (bookId && root(state).renderTree.error[bookId]) || null;

export const selectPreflightEntry = (state, selector) =>
  (selector && root(state).preflight.bySelector[selector]) || null;

export const selectPreflight = (state, selector) =>
  selectPreflightEntry(state, selector)?.preflight || null;

export const selectPreflightLoading = (state, selector) =>
  !!(selector && root(state).preflight.loading[selector]);

export const selectPreflightError = (state, selector) =>
  (selector && root(state).preflight.error[selector]) || null;

export const selectStitchReadinessEntry = (state, bookId) =>
  (bookId && root(state).stitchReadiness.byBookId[bookId]) || null;

export const selectStitchReadiness = (state, bookId) =>
  selectStitchReadinessEntry(state, bookId)?.readiness || null;

export const selectStitchReadinessLoading = (state, bookId) =>
  !!(bookId && root(state).stitchReadiness.loading[bookId]);

export const selectStitchReadinessError = (state, bookId) =>
  (bookId && root(state).stitchReadiness.error[bookId]) || null;

// ---- Phase 4d R3c — stitch engine job selectors ---------------------------

export const selectStitchJob = (state) => root(state).stitchJob.current;
export const selectStitchJobLoading = (state) => !!root(state).stitchJob.loading;
export const selectStitchJobError = (state) => root(state).stitchJob.error || null;
export const selectStitchJobBlocked = (state) =>
  root(state).stitchJob.blocked || null;

/**
 * True while a stitch job is still assembling. The server emits
 * `{ status: 'running' | 'complete' | 'failed' }`; anything outside the
 * terminal set counts as in-flight. Mirrors `selectUnitJobInFlight`.
 */
export const selectStitchJobInFlight = (state) => {
  const job = root(state).stitchJob.current;
  if (!job) return false;
  const terminal = ['complete', 'completed', 'failed', 'cancelled'];
  return !terminal.includes(job.status);
};

export const selectUnitJob = (state) => root(state).unitJob.current;
export const selectUnitJobLoading = (state) => !!root(state).unitJob.loading;
export const selectUnitJobError = (state) => root(state).unitJob.error || null;
export const selectUnitJobPreflightBlocked = (state) =>
  root(state).unitJob.preflightBlocked || null;

/**
 * Convenience: true while a unit-render job is still in flight. Mirrors
 * `selectRenderJobInFlight` for the selector-driven pipeline. The R3a
 * server emits `{ status: 'queued' | 'rendering' | 'completed' | 'failed' }`,
 * so anything outside the terminal set (`completed`, `failed`, `cancelled`)
 * counts as in-flight.
 */
export const selectUnitJobInFlight = (state) => {
  const job = root(state).unitJob.current;
  if (!job) return false;
  const terminal = ['completed', 'complete', 'failed', 'cancelled'];
  return !terminal.includes(job.status);
};

export const selectRenderedLinesEntry = (state, selector) =>
  (selector && root(state).renderedLines.bySelector[selector]) || null;

export const selectRenderedLineRows = (state, selector) =>
  selectRenderedLinesEntry(state, selector)?.rows || EMPTY_ARRAY;

export const selectRenderedLineSet = (state, selector) =>
  selectRenderedLinesEntry(state, selector)?.lineSet || EMPTY_ARRAY;

export const selectRenderedLinesReason = (state, selector) =>
  selectRenderedLinesEntry(state, selector)?.reason || null;

export const selectRenderedLinesLoading = (state, selector) =>
  !!(selector && root(state).renderedLines.loading[selector]);

export const selectRenderedLinesError = (state, selector) =>
  (selector && root(state).renderedLines.error[selector]) || null;


export default slice.reducer;


